import { forwardRef, useRef } from 'react'
import merge from 'deepmerge'
import { array, bool, number, object, oneOfType, string } from 'prop-types'
import ReactApexChart from 'react-apexcharts'
import useDeepCompareEffect from 'use-deep-compare-effect'

import CustomChartLegend from 'components/CustomChartLegend'
import { COLORS } from 'consts'
import { useIsMobileDevice } from 'hooks'
import { FeatureFlagsStore } from 'stores'
import { humanizeLargeNumber } from 'utils'
import { getOptionsBasedCustomLegend } from 'utils/chartOptions'

import './index.scss'
import { useChartPrint } from '../../contexts/ChartPrintContext'

const ColumnGraph = forwardRef(
  ({ categories, data, colors, options, width, height, enableCustomLegends = true }, ref) => {
    const { setChartsConfig } = useChartPrint()
    const isCustomChartLegendDisabled = FeatureFlagsStore.isFeatureFlagEnabled('isCustomChartLegendDisabled')

    const isMobileDevice = useIsMobileDevice()

    const shouldEnableCustomLegends = enableCustomLegends && !isCustomChartLegendDisabled && isMobileDevice

    const chartRef = useRef(null)
    const definitiveRef = ref ?? chartRef

    const getDefaultOptions = ({ categories, colors }) => {
      return {
        colors: colors.length ? colors : [COLORS.primary],
        plotOptions: {
          bar: {
            distributed: true,
          },
        },
        xaxis: {
          categories: categories,
        },
        yaxis: {
          labels: {
            formatter: function (val) {
              if (val > 1_000_000) {
                return humanizeLargeNumber(val, 1)
              }
              return humanizeLargeNumber(val)
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
        legend: {
          markers: {
            shape: 'circle',
            size: 6,
          },
        },
        chart: {
          toolbar: {
            tools: {
              download: false,
            },
          },
          animations: {
            enabled: false,
          },
        },
      }
    }

    const defaultOptions = getDefaultOptions({ categories, colors })
    const mergedOptions = merge.all([defaultOptions, options], {
      arrayMerge: (destination, source) => source,
    })
    const newOptions = getOptionsBasedCustomLegend({
      options: mergedOptions,
      enableCustomLegends: shouldEnableCustomLegends,
    })

    const legends = categories?.map(category => category?.split(':')[0])?.filter(Boolean)

    const isLegendsEnabled = mergedOptions?.legend?.show ?? true
    const shouldShowCustomLegend = shouldEnableCustomLegends && isLegendsEnabled

    useDeepCompareEffect(() => {
      const config = {
        options: {
          chart: {
            chartType: 'column',
          },
          colors,
          categories: categories,
        },
        colors,
        series: data,
        categories: categories,
      }
      setChartsConfig([config])
    }, [categories, setChartsConfig, categories, colors, data])

    return (
      <div className="ColumnGraph">
        <ReactApexChart
          type="bar"
          height={height}
          width={width}
          options={newOptions}
          series={data}
          ref={definitiveRef}
        />
        {shouldShowCustomLegend && (
          <CustomChartLegend legends={legends} chartRef={definitiveRef} limit={3} disableClick />
        )}
      </div>
    )
  }
)

ColumnGraph.propTypes = {
  categories: array.isRequired,
  data: array.isRequired,
  colors: array.isRequired,
  options: object.isRequired,
  height: oneOfType([string, number]),
  width: oneOfType([string, number]),
  enableCustomLegends: bool,
}

ColumnGraph.defaultProps = {
  categories: [],
  data: [],
  colors: [],
  options: {},
  height: 300,
  width: '100%',
  enableCustomLegends: true,
}

export default ColumnGraph
